/**
 * State machine for the Practice page exercise loop.
 *
 * Pure: no React, no audio, no I/O. The page wires audio engine callbacks
 * and FSRS persistence to dispatched actions; this module owns the legal
 * transitions and the invariants between them.
 *
 * Phases:
 *   ready    → no exercise yet, waiting for Start
 *   playing  → audio engine is rendering the cadence + target
 *   awaiting → playback finished, waiting for the user to answer
 *   graded   → user answered, feedback shown
 *
 * Invariants (enforced by the reducer, not the dispatcher):
 *   - ANSWER_SUBMITTED is a no-op outside `awaiting`, and a no-op if the
 *     current exercise was already answered. This prevents a second FSRS
 *     review write when the user answers, replays, then answers again.
 *   - PLAYBACK_FINISHED only advances state when we were actually playing.
 *   - REPLAY_STARTED only fires from a settled phase (awaiting / graded).
 */

import type { Exercise, ExerciseAnswer, GradingResult } from "@music-app/exercise-generator";

export type Phase = "ready" | "playing" | "awaiting" | "graded";

export interface PracticeState {
  phase: Phase;
  exercise: Exercise | null;
  /** Accumulated answer during awaiting; set on submit; null on new exercise. */
  pendingAnswer: ExerciseAnswer | null;
  /** Populated after submission; null during awaiting or on new exercise. */
  gradingResult: GradingResult | null;
  counts: { correct: number; total: number };
  /** performance.now() captured at the moment we entered `awaiting`. */
  awaitingStartedAt: number;
  /** Has the current exercise already been graded? Reset on a new exercise. */
  hasAnswered: boolean;
}

export type PracticeAction =
  | { type: "EXERCISE_GENERATED"; exercise: Exercise }
  | { type: "PLAYBACK_FINISHED"; at: number }
  | { type: "REPLAY_STARTED" }
  | { type: "ANSWER_CHANGE"; answer: ExerciseAnswer }
  | { type: "ANSWER_SUBMITTED"; answer: ExerciseAnswer; result: GradingResult };

export const initialPracticeState: PracticeState = {
  phase: "ready",
  exercise: null,
  pendingAnswer: null,
  gradingResult: null,
  counts: { correct: 0, total: 0 },
  awaitingStartedAt: 0,
  hasAnswered: false,
};

export function practiceReducer(state: PracticeState, action: PracticeAction): PracticeState {
  switch (action.type) {
    case "EXERCISE_GENERATED":
      return {
        ...state,
        phase: "playing",
        exercise: action.exercise,
        pendingAnswer: null,
        gradingResult: null,
        hasAnswered: false,
      };

    case "PLAYBACK_FINISHED":
      if (state.phase !== "playing") return state;
      // If the user replayed *after* answering, return to "graded" so the
      // Next-question button stays available. Otherwise (first playback,
      // or replay before answering) settle into "awaiting" and capture
      // the response-time origin.
      if (state.hasAnswered) {
        return { ...state, phase: "graded" };
      }
      return { ...state, phase: "awaiting", awaitingStartedAt: action.at };

    case "REPLAY_STARTED":
      if (state.phase !== "awaiting" && state.phase !== "graded") return state;
      return { ...state, phase: "playing" };

    case "ANSWER_CHANGE":
      return { ...state, pendingAnswer: action.answer };

    case "ANSWER_SUBMITTED": {
      if (state.phase !== "awaiting" || state.hasAnswered) return state;
      return {
        ...state,
        phase: "graded",
        pendingAnswer: action.answer,
        gradingResult: action.result,
        hasAnswered: true,
        counts: {
          correct: state.counts.correct + (action.result.correct ? 1 : 0),
          total: state.counts.total + 1,
        },
      };
    }
  }
}

/** Convenience: derive whether the answer buttons should accept input. */
export function canAnswer(state: PracticeState): boolean {
  return state.phase === "awaiting" && !state.hasAnswered;
}

/** Convenience: derive whether replay is allowed. */
export function canReplay(state: PracticeState): boolean {
  return state.phase === "awaiting" || state.phase === "graded";
}
